/**
 * Marketing homepage — the Nocturne redesign. Shows the product first: a
 * rendered dashboard mockup (drawn from the design system, not a screenshot,
 * so it never goes stale) sits beside a benefit-led hero, then a screen-by-
 * screen tour of the live web app (real screenshots, demo-account sample
 * data only — never a member's money), the phone and watch strip with the
 * live store links, what is new since launch, the one saturated stat band
 * Nocturne allows, how-it-works, the six differentiators, security, one-plan
 * pricing, invite-and-earn and a low-key waitlist close. The nav carries the
 * licensed theme-song player.
 *
 * Behaviour is preserved from the previous Landing: pre-launch the CTAs gate
 * behind `showAuth` (signed-in or ?access= link), the waitlist posts to
 * /auth/waitlist/, and the theme plays from /vault-theme.mp3 on tap only
 * (never autoplay). All styling comes from vault-home.css, whose Nocturne
 * token set is scoped to `.vhome` so nothing global shifts.
 *
 * The trial is cardless (since 2026-09-01): no card is asked for at signup,
 * so no copy may mention a signup card check. The card is captured on the
 * paywall, in the same step that starts the subscription.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App.jsx';
import { api } from '../api.js';
import { earlyAccess } from '../early.js';
import './vault-home.css';

/* Rendered-dashboard data — mirrors Vault Map and Money.dc.html exactly; a
   later pass can bind these to the live budget so the shot tracks the app. */
const BANKS = [
  { mono: 'TB', name: 'TymeBank', amt: 'R 34 283', tone: 'up' },
  { mono: 'CB', name: 'Capitec', amt: '-R 420', tone: 'down' },
  { mono: 'DB', name: 'Discovery', amt: 'R 1 899', tone: 'up' },
];
const POTS = [
  { k: 'Savings pot', v: 'R 2 336', s: 'TymeBank · 47% of target', cls: 'accent' },
  { k: 'Cash on hand', v: 'R 850', s: 'wallet · 1 money spot', cls: '' },
  { k: 'Monthly check', v: '8 notes', s: '3 clear in one tap', cls: 'warn' },
];
const OUT_ROWS = [
  { name: 'Spending Money', meta: 'TymeBank · = 200*31', amt: 'R 6 200', paid: false },
  { name: 'Biltong', meta: 'TymeBank · = 412*4', amt: 'R 1 648', paid: false },
  { name: 'Mom', meta: 'Discovery Bank · Family', amt: 'R 8 800', paid: true },
];

const STATS = [
  ['R 35 762', 'the number Vault exists to keep honest — your real leftover, not your balance'],
  ['Your banks', 'as many or as few as you actually use — each with its own leftover, so a short account cannot hide behind a healthy total'],
  ['Payday', 'your month starts the day you are paid, and waits if you are paid late'],
  ['Web + apps', 'live on the web and the App Store for iPhone and iPad; Android and Wear OS are in testing — logging offline and syncing after'],
];

const STEPS = [
  ['1', 'Import — or start fresh', 'Upload any SA bank statement as a PDF — even password-protected — or an Excel file, and Vault defines your budget from what it sees: paid items ticked, income flagged, recurring bills and formulas worked out.'],
  ['2', 'Run it your way', 'Type one word and AI fills the category, note, bank and whether it recurs. Pay formula budgets off unit by unit, log a spend at a shop, move money between banks. Every rand is a logged line you can name and delete.'],
  ['3', 'On web, phone and watch', 'Web to plan, phone to log on the move, watch to pay a unit from your wrist. It works offline and syncs when you are back — the same Vault everywhere, always in step.'],
];

const FEATURES = [
  ['🧮', 'Formula budgets', 'R200 a day for the month is 200*31 — thirty-one payable units. Tick them off one at a time as you actually spend, instead of guessing at a single monthly number.'],
  ['📍', 'The Spend Map', 'Attach expenses to real places. See the month by neighbourhood, and get a nudge on your wrist when you arrive somewhere you normally spend.'],
  ['📅', 'Payday months', 'Your month rolls over when your salary lands, not on the 1st. Paid late? The budget waits with you and carries the leftover across.'],
  ['🧾', 'A tax pack your accountant can use', 'Flag as you go. Vault sorts it into SARS buckets, converts every receipt to PDF, keeps a business travel logbook, and hands the lot over in one link.'],
  ['💵', 'Cash counts too', 'A wallet, a safe, an envelope — add a money spot and Vault tracks the cash you hold beside your accounts. Move money in from a bank, spend from it, and every move is a line you can reverse.'],
  ['🏦', 'One account or six', 'You name the accounts you actually use — there is no fixed number and no minimum. Add one to start, add another when you open it, archive one and its history stays.'],
];

const TRUST = [
  ['🔒 Encrypted in transit', 'TLS on every connection, enforced with HSTS. Nothing travels plain.'],
  ['💳 Your card never touches us', 'The trial needs no card. When you subscribe, Paystack handles your card end to end — we store only a payment token.'],
  ['🧱 Your data is walled off', 'Every row belongs to your account alone — enforced in the schema and proven by automated tests on each release.'],
  ['🔑 Strong sign-in', 'Argon2 password hashing, Google and Apple sign-in, and short-lived sessions revocable server-side.'],
  ['🇿🇦 POPIA-first', 'Consent at signup, AI only with your permission, export everything free any time, and delete your account and every trace in one action.'],
  ['💾 Backed up', 'The database is backed up before every release, and receipts sit behind authentication — never on public URLs.'],
];

const INCLUDED = [
  'As many accounts as you actually use — two at the same bank too',
  'Cash tracked beside your banks, in wallets and money spots',
  'Import statements from any South African bank',
  'Formula budgeting, paid off unit by unit',
  'The interactive Spend Map with visit and trip tracking',
  'Recurring expenses and income — starting this month or next',
  'AI receipt scanning, paste-in slips and voice — only with your permission',
  'Savings pots with deposits and withdrawals',
  'The Monthly Check — short banks and spare cash, in one tap',
  'A tax pack with a SARS travel logbook, emailed to your accountant',
  'An optional nightly WhatsApp recap',
  'Invite friends on WhatsApp for up to 30 extra free days',
  'Web, iPhone and iPad today — Android phone and tablet, and Wear OS, in testing',
];

const REFER_STEPS = [
  ['1', 'Send your link from Account · Invite friends, straight into a WhatsApp chat.'],
  ['2', 'They sign up with your link — no card needed — and start with 5 welcome days on top of their free trial.'],
  ['3', 'Your own free time grows by 6 days for every friend who joins, up to 30. Both of you see the days on the Account page.'],
];

// The price line under the hero CTA — the trial is cardless.
const TRIAL_LINE = 'No card needed to start. Then R59/month or R590/year — two months free. Cancel anytime.';

// Store links. An empty URL hides that button — the page only lists what is live.
// The App Store listing (iPhone and iPad, version 1.0.3) verified public
// 2026-10-02. Android and Wear OS are on Google Play's closed testing track only
// (never in production), so the page says "in testing" and sends Android people
// to /android-testers instead of a Play button. The Apple Watch app is not on
// the App Store yet, so no copy claims it.
const APP_STORE_URL = 'https://apps.apple.com/za/app/id6795021332';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.maregreyling.vault';
const WEAR_STORE_URL = 'https://play.google.com/store/apps/details?id=com.maregreyling.vaultwear';

/* Screen-by-screen tour. Every image is a real screenshot of the live web app
   (vault-app.co.za), taken from the demo account's seeded sample data so no
   member's money is ever shown. Files live in /public/app-shots/. */
const TOUR = [
  {
    shots: [['web-dashboard', 'The Vault dashboard: real leftover across three banks, one flagged short']],
    url: 'vault-app.co.za/app',
    kicker: 'Dashboard',
    title: 'Every bank tells its own truth',
    body: 'Your real leftover sits at the top — the balance now, minus what is still to pay. Each bank carries its own leftover, and one that cannot cover what is due is flagged short. Your savings pot, cash in wallets and money spots, and the Monthly Check sit right below.',
  },
  {
    shots: [['web-money-out', 'Money out and money in: ticked expenses, formula progress and an event due today']],
    url: 'vault-app.co.za/app',
    kicker: 'Money out, money in',
    title: 'Formula budgets, ticked off as you go',
    body: 'Every expense is a line you can tick. A formula budget such as Spending Money shows how many units are paid and what is left, and a shop event due today is flagged so you log it on the spot. Salary lands under Money in, and your month starts there.',
  },
  {
    shots: [['web-spend-map', 'The Spend Map: spending bubbles over Cape Town beside a list of shops by amount']],
    url: 'vault-app.co.za/app',
    kicker: 'Spend Map',
    title: 'See where your money actually goes',
    body: 'Your month by place: every shop you spend at, how much and how many visits, with daily shop events and trips beside the map. Search a place, filter by bank, or switch to what is still coming.',
    credit: 'Map data © OpenStreetMap contributors',
  },
  {
    shots: [['web-insights', 'Spending insights: what Vault noticed, and spend by category, expense and shop']],
    url: 'vault-app.co.za/insights',
    kicker: 'Insights',
    title: 'What Vault noticed this month',
    body: 'Spending by category, your top expenses and the shops you use most, at a glance — plus plain-language notes: your biggest spend, money you could move to savings, and where most of it went.',
  },
  {
    shots: [
      ['web-tax-pack', 'The tax pack: readiness score and the checks still to finish'],
      ['web-logbook', 'The travel logbook: business trips valued at the SARS rate'],
    ],
    url: 'vault-app.co.za/tax',
    kicker: 'Tax pack',
    title: 'A tax pack your accountant can use',
    body: 'Flag deductible items as you go and Vault sorts them into SARS buckets for the tax year. The readiness check shows what is missing — a receipt, a client on a trip, a certificate — and business trips are valued at the SARS per-kilometre rate. Email the whole pack to your accountant in one go.',
  },
];

/* What has shipped since launch day (2026-08-29) — live features only. */
const NEW_SINCE_LAUNCH = [
  ['📱', 'On iPhone and iPad', 'Vault is on the App Store for iPhone and iPad, beside the web — one account, the same figures everywhere. Android and Wear OS are in testing.'],
  ['🌱', 'A simpler view', 'Switch on Simple view for just the essentials: safe to spend today, log a spend, and tick what’s paid. Show everything brings the full Vault back, and nothing is lost.'],
  ['💳', 'Pick the bank for a spend', 'Paid from a different account this time? Choose the bank when you log the spend in Simple view, and only that bank goes down.'],
  ['🎁', 'No card to start', 'The 5-day trial needs no card at all. You add one only when you choose to subscribe.'],
  ['🗓️', 'Monthly or yearly', 'R59 a month, or R590 a year — two months free. Switch at renewal with no double charge, and cancel or restart from Account.'],
  ['🏦', 'Two accounts at one bank', 'Add the same bank twice with its own account name — each one keeps its own balance and leftover.'],
  ['🤖', 'You decide what AI sees', 'Vault shows exactly what it would send to an AI service and to whom, and asks first. Withdraw that permission any time.'],
  ['📋', 'Paste a receipt', 'Copy a slip and paste it straight in, or scan the QR code on a Yoco slip. AI reads the shop, amount and date before anything saves.'],
  ['🚗', 'Trips and a SARS logbook', 'Log a business trip and name the client. Vault values it at the SARS per-kilometre rate and holds back any trip without a client.'],
  ['📧', 'Email the tax pack', 'Totals, the logbook, receipts as PDFs and your certificates — attached to one email to your accountant, or to yourself.'],
  ['🔁', 'Recurring, from when you say', 'A new recurring item asks: this month too, or from next month? This month is only counted if you say so.'],
  ['💸', 'Payday, on your terms', 'When a new month opens, Vault asks before carrying last month’s leftover into each bank — or set it to do that automatically.'],
  ['💬', 'A nightly WhatsApp recap', 'Opt in for a short end-of-day summary of your money, sent to WhatsApp.'],
];

export default function Landing() {
  const { me } = useAuth();
  // LAUNCHED: public sign-up is open. Set to false to return to invite-only
  // pre-launch (CTAs gate behind sign-in or an ?access= link again).
  const LAUNCHED = true;
  const showAuth = LAUNCHED || !!me || earlyAccess();
  const [menuOpen, setMenuOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [wlEmail, setWlEmail] = useState('');
  const [wlDone, setWlDone] = useState(false);
  const [wlErr, setWlErr] = useState('');
  const [tsName, setTsName] = useState('');
  const [tsEmail, setTsEmail] = useState('');
  const [tsPhone, setTsPhone] = useState('');
  const [tsPlatform, setTsPlatform] = useState('');
  const [tsWa, setTsWa] = useState(false);
  const [tsConsent, setTsConsent] = useState(false);
  const [tsDone, setTsDone] = useState(false);
  const [tsErr, setTsErr] = useState('');
  const audioRef = useRef(null);
  const closeMenu = () => setMenuOpen(false);

  // Theme song — plays on tap only (never autoplay); a second tap pauses.
  const toggleTheme = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => {}); }
    else { a.pause(); setPlaying(false); }
  };

  const refCode = me?.ref_code || me?.referral_code || null;
  const refLink = refCode ? `vault-app.co.za/?ref=${refCode}` : 'vault-app.co.za';
  const waHref = `https://wa.me/?text=${encodeURIComponent(`I'm budgeting with Vault — payday-first, an honest leftover per account. Try it: https://${refLink}`)}`;

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setWlErr('');
    try {
      await api('/auth/waitlist/', { method: 'POST', body: { email: wlEmail, source: 'landing' } });
      setWlDone(true);
    } catch (err) {
      setWlErr(err?.data?.detail || 'Could not save — try again.');
    }
  };

  const joinTester = async (e) => {
    e.preventDefault();
    setTsErr('');
    if (!tsConsent) { setTsErr('Please tick the consent box to sign up.'); return; }
    try {
      await api('/auth/tester-signup/', {
        method: 'POST',
        body: {
          name: tsName, email: tsEmail, phone: tsPhone, platform: tsPlatform,
          whatsapp_optin: tsWa, consent: tsConsent,
        },
      });
      setTsDone(true);
    } catch (err) {
      setTsErr(err?.data?.detail || 'Could not sign you up — try again.');
    }
  };

  const Player = (
    <button type="button" className="vh-player" aria-pressed={playing}
            aria-label={playing ? 'Pause the Vault theme' : 'Play the Vault theme — What’s Left Is Real'}
            onClick={toggleTheme}>
      <span className="glyph" aria-hidden="true">{playing ? '❙❙' : '▶'}</span>
      <span className="vh-bars" aria-hidden="true"><i /><i /><i /><i /></span>
      <span className="label">{playing ? 'Playing the Vault theme' : 'Play the theme'}</span>
    </button>
  );

  return (
    <div className="vhome vh-page">
      <audio ref={audioRef} src="/vault-theme.mp3" preload="none" onEnded={() => setPlaying(false)} />

      {/* 1 · Nav */}
      <header className="vh-nav">
        <div className="vh-brand">
          <img src="/logo.svg" alt="" />
          <span className="vh-brand-word">Vault</span>
        </div>
        <div className="vh-navwrap">
          <nav className="vh-navlinks">
            <a href="#vh-tour">The app</a>
            <a href="#vh-new">What&rsquo;s new</a>
            <a href="#vh-how">How it works</a>
            <a href="#vh-security">Security</a>
            <a href="#vh-pricing">Pricing</a>
            {Player}
            {!me && showAuth && <Link to="/login" className="vh-btn vh-btn-ghost vh-btn-md">Sign in</Link>}
          </nav>

          {me ? (
            <Link to="/app" className="vh-btn vh-btn-primary vh-btn-md">Open my Vault</Link>
          ) : showAuth ? (
            <Link to="/register" className="vh-btn vh-btn-primary vh-btn-md">Start free</Link>
          ) : (
            <span className="vh-nav-soon">Launching soon</span>
          )}

          <button type="button" className="vh-hamburger" aria-label="Menu"
                  aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>☰</button>
          {menuOpen && (
            <div className="vh-menu" role="menu">
              <a href="#vh-tour" onClick={closeMenu}>The app</a>
              <a href="#vh-new" onClick={closeMenu}>What&rsquo;s new</a>
              <a href="#vh-how" onClick={closeMenu}>How it works</a>
              <a href="#vh-security" onClick={closeMenu}>Security</a>
              <a href="#vh-pricing" onClick={closeMenu}>Pricing</a>
              {Player}
              {!me && showAuth && <Link to="/login" onClick={closeMenu}>Sign in</Link>}
            </div>
          )}
        </div>
      </header>

      {/* 2 · Hero */}
      <section className="vh-hero">
        <div className="vh-hero-copy">
          <div className="vh-badge">
            <span className="dot" />
            <span>Live on the web &amp; App Store · Android in testing · built in South Africa</span>
          </div>
          <h1 className="vh-h1">Know what&rsquo;s left,<br /><span className="accent">in every bank.</span></h1>
          <p className="vh-lead">
            Vault is payday-first budgeting. It subtracts what you still owe from what you
            actually have — per account, honestly, all month. Your month starts the day
            you&rsquo;re paid, not on the 1st.
          </p>
          <div className="vh-cta-row">
            {showAuth ? (
              <Link to="/register" className="vh-btn vh-btn-primary vh-btn-lg">Start your 5-day free trial</Link>
            ) : (
              <span className="vh-btn vh-btn-primary vh-btn-lg vh-btn-disabled">Launching soon</span>
            )}
            <a href="#vh-tour" className="vh-btn vh-btn-secondary vh-btn-lg">See the app</a>
          </div>
          <p className="vh-price-line">
            {showAuth ? TRIAL_LINE : 'Public sign-up opens at launch — then R59/month after a 5-day free trial.'}
          </p>
          {/* Not-a-bank disclaimer sits BELOW the CTA, never in the lead. */}
          <p className="vh-disclaimer">
            Vault is an independent budgeting app — not a bank, not affiliated with one. We
            never ask for your online-banking login: you import your own statements or add
            entries yourself.
          </p>
        </div>

        <div className="vh-hero-shot">
          {/* Rendered dashboard mockup — built from the design system, not a screenshot. */}
          <div className="vh-shot">
            <div className="vh-chrome">
              <div className="dots"><i /><i /><i /></div>
              <div className="url">vault-app.co.za/app</div>
            </div>
            <div className="vh-shot-body">
              <div className="vh-shot-head">
                <img src="/logo.svg" alt="" />
                <span className="vh-shot-brand">Vault</span>
                <span className="vh-shot-month">&lsaquo; August 2026 &rsaquo;</span>
                <div className="vh-shot-tabs"><span>Map</span><span>Insights</span><span>Tax</span><span>Manage</span></div>
              </div>

              <div className="vh-leftover">
                <div className="label">Real leftover across your banks</div>
                <div className="big">R 35 762,81</div>
                <div className="sub">balance now R 50 717,00 · still to pay R 14 954,19</div>
              </div>

              <div className="vh-bankgrid">
                {BANKS.map((b) => (
                  <div className="vh-bank" key={b.name}>
                    <div className="row"><span className="mono">{b.mono}</span><span className="name">{b.name}</span></div>
                    <div className={`amt ${b.tone}`}>{b.amt}</div>
                    <div className="cap">leftover now</div>
                  </div>
                ))}
              </div>

              {/* three-up: savings pot / cash on hand / monthly check */}
              <div className="vh-pots">
                {POTS.map((p) => (
                  <div className={`vh-pot ${p.cls}`} key={p.k}>
                    <div className="k">{p.k}</div>
                    <div className="v">{p.v}</div>
                    <div className="s">{p.s}</div>
                  </div>
                ))}
              </div>

              <div className="vh-cols">
                <div>
                  <div className="vh-col-h">▼ Money out</div>
                  <div className="vh-rows">
                    {OUT_ROWS.map((r) => (
                      <div className="vh-orow" key={r.name}>
                        <span className={`vh-tick ${r.paid ? 'paid' : ''}`} />
                        <div className="body">
                          <div className={`name ${r.paid ? 'paid' : ''}`}>{r.name}</div>
                          <div className="meta">{r.meta}</div>
                        </div>
                        <div className="amt">{r.amt}</div>
                      </div>
                    ))}
                  </div>
                  {/* The Spend-Map event is an EXPENSE — it belongs under Money out. */}
                  <div className="vh-gaal">
                    <div className="t">📍 Spending Money @ Gaal Mini Market</div>
                    <div className="b">standing event · 1 of 2 visits today · R 12,00 each</div>
                  </div>
                </div>
                <div>
                  <div className="vh-col-h in">▲ Money in</div>
                  <div className="vh-inrow">
                    <span className="check">✓</span>
                    <div className="body">
                      <div className="name">Salary</div>
                      <div className="meta">Discovery Bank · ★ master</div>
                    </div>
                    <div className="amt">R 57 165</div>
                  </div>
                  {/* A second salary, not yet received — Money in shows real income only. */}
                  <div className="vh-inrow open">
                    <span className="check" />
                    <div className="body">
                      <div className="name">Salary and Savings</div>
                      <div className="meta">TymeBank · lands day 25</div>
                    </div>
                    <div className="amt">R 27 006</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="vh-devices">
            <div className="vh-phone">
              <div className="screen">
                <div className="lbl">Left to spend</div>
                <div className="amt">R 35 762</div>
                <div className="bar"><i /></div>
                <div className="chip">Spending Money</div>
                <div className="chip">Biltong</div>
                <div className="segs"><i className="on" /><i /><i /></div>
              </div>
            </div>
            <div className="vh-watch">
              <div className="screen">
                <div className="ring"><div>R35k</div></div>
                <div className="cap">R 282 a day</div>
              </div>
            </div>
          </div>
          <p className="vh-apps-note"><strong>Live now</strong> on the web and on the <a href={APP_STORE_URL} target="_blank" rel="noreferrer">App&nbsp;Store</a> for iPhone and iPad. Android phone and tablet, plus Wear&nbsp;OS, are in testing on Google&nbsp;Play — <Link to="/android-testers">join the Android testers</Link>.</p>
        </div>
      </section>

      {/* 2b · A closer look — real screens from the live web app */}
      <section className="vh-section vh-tour" id="vh-tour">
        <div className="vh-head">
          <div className="vh-kicker">A closer look</div>
          <h2 className="vh-h3">The live app, screen by screen</h2>
          <p className="vh-sub">Real screens from vault-app.co.za, shown with a demo account&rsquo;s sample figures &mdash; never a member&rsquo;s money.</p>
        </div>
        <div className="vh-tour-list">
          {TOUR.map((t, i) => (
            <div className={`vh-tour-row${i % 2 ? ' flip' : ''}`} key={t.kicker}>
              <div className="vh-tour-shots">
                {t.shots.map(([f, alt]) => (
                  <figure className="vh-frame" key={f}>
                    <div className="vh-chrome" aria-hidden="true">
                      <div className="dots"><i /><i /><i /></div>
                      <div className="url">{t.url}</div>
                    </div>
                    <a href={`/app-shots/${f}.webp`} target="_blank" rel="noreferrer" aria-label={`${alt} — open full size`}>
                      <img src={`/app-shots/${f}.webp`} alt={alt} loading="lazy" />
                    </a>
                  </figure>
                ))}
                {t.credit && <div className="vh-tour-credit">{t.credit}</div>}
              </div>
              <div className="vh-tour-copy">
                <div className="vh-kicker">{t.kicker}</div>
                <h3 className="vh-tour-title">{t.title}</h3>
                <p className="vh-tour-body">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 2c · Phone and watch — real screens from the shipping apps */}
      <section className="vh-section vh-shots" id="vh-shots">
        <div className="vh-head">
          <div className="vh-kicker">On your phone and wrist</div>
          <h2 className="vh-h3">Log it where you spend it</h2>
          <p className="vh-sub">Screens from the shipping phone and watch apps.</p>
        </div>
        <div className="vh-appshot-row">
          {[
            ['phone-home', 'Home \u2014 safe to spend today'],
            ['phone-budget', 'Budget \u2014 every bank, its own truth'],
            ['phone-map', 'Spend Map \u2014 log where you are'],
            ['phone-cash', 'Cash on hand'],
            ['watch-home', 'On your wrist'],
          ].map(([f, alt]) => (
            <figure className="vh-appshot" key={f}>
              <img src={`/app-shots/${f}.png`} alt={alt} loading="lazy" />
              <figcaption>{alt}</figcaption>
            </figure>
          ))}
        </div>
        <div className="vh-getrow">
          {showAuth
            ? <Link to="/register" className="vh-store vh-store-web">Use Vault on the web &rarr;</Link>
            : <span className="vh-store vh-store-web">Web &mdash; live now</span>}
          {APP_STORE_URL && <a href={APP_STORE_URL} className="vh-store" target="_blank" rel="noreferrer">Download on the App Store</a>}
          <Link to="/android-testers" className="vh-store">Android &amp; Wear OS &mdash; join testing</Link>
        </div>
      </section>

      {/* 2d · New since launch — shipped features only */}
      <section className="vh-section vh-new" id="vh-new">
        <div className="vh-head">
          <div className="vh-kicker">New since launch</div>
          <h2 className="vh-h3">Added since launch day</h2>
          <p className="vh-sub">All live now, in the same one plan.</p>
        </div>
        <div className="vh-new-grid">
          {NEW_SINCE_LAUNCH.map(([icon, t, b]) => (
            <div className="vh-card vh-feature vh-newcard" key={t}>
              <div className="tile">{icon}</div>
              <div>
                <div className="t">{t}</div>
                <div className="b">{b}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3 · Stat band — the single saturated flood */}
      <section className="vh-stats">
        {STATS.map(([v, k]) => (
          <div className="vh-stat" key={v}>
            <div className="v">{v}</div>
            <div className="k">{k}</div>
          </div>
        ))}
      </section>

      {/* 4 · How it works */}
      <section className="vh-section" id="vh-how">
        <div className="vh-head">
          <div className="vh-kicker">How it works</div>
          <h2 className="vh-h3">Set it up in minutes — then run it your way</h2>
          <p className="vh-sub">Import your statements and Vault builds the first month for you, or start empty and add as you go. Importing is a shortcut, never a requirement.</p>
        </div>
        <div className="vh-grid-3">
          {STEPS.map(([n, t, b]) => (
            <div className="vh-card vh-step" key={n}>
              <div className="vh-kicker">{n}</div>
              <div className="t">{t}</div>
              <div className="b">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 5 · Why it's different — six cards */}
      <section className="vh-section">
        <div className="vh-head">
          <div className="vh-kicker">Why it&rsquo;s different</div>
          <h2 className="vh-h3">Six things no other budgeting app does</h2>
        </div>
        <div className="vh-grid-4">
          {FEATURES.map(([icon, t, b]) => (
            <div className="vh-card vh-feature" key={t}>
              <div className="tile">{icon}</div>
              <div>
                <div className="t">{t}</div>
                <div className="b">{b}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 6 · Security */}
      <section className="vh-section" id="vh-security">
        <div className="vh-head">
          <div className="vh-kicker">Security</div>
          <h2 className="vh-h3">Built like a vault, named like one</h2>
        </div>
        <div className="vh-grid-6">
          {TRUST.map(([t, b]) => (
            <div className="vh-card vh-trust" key={t}>
              <div className="t">{t}</div>
              <div className="b">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 7 · Pricing */}
      <section className="vh-section" id="vh-pricing">
        <div className="vh-card vh-price">
          <div className="vh-kicker">Pricing</div>
          <div className="plan">One plan. Everything in it.</div>
          <div className="amount"><span className="n">R59</span><span className="per">/month</span></div>
          <div className="vh-price-year" style={{ marginTop: 6, fontSize: '0.95rem', opacity: 0.75 }}>or <strong>R590/year</strong> — two months free</div>
          <div className="vh-incl">
            {INCLUDED.map((i) => (
              <div className="vh-incl-item" key={i}>
                <span className="mk">✓</span>
                <span className="tx">{i}</span>
              </div>
            ))}
          </div>
          {showAuth ? (
            <Link to="/register" className="vh-btn vh-btn-primary vh-btn-lg">Start your 5-day free trial</Link>
          ) : (
            <span className="vh-btn vh-btn-primary vh-btn-lg vh-btn-disabled">Launching soon</span>
          )}
          <p className="vh-statement">Prices shown are for the web and Android. In the iPhone and iPad app, Vault Pro is bought through the App Store at R59,99/month or R599,99/year.</p>
          <p className="vh-statement">No card is needed for the trial. Vault is a Maré Greyling product &mdash; on the web and Android your subscription appears as &ldquo;Mare Greyling&rdquo; on your card statement.</p>
        </div>
      </section>

      {/* 8 · Invite & earn */}
      <section className="vh-section">
        <div className="vh-card vh-refer">
          <div className="vh-refer-main">
            <div className="vh-kicker">Invite &amp; earn</div>
            <div className="vh-refer-title">Bring a friend, you both get free days</div>
            <div className="vh-refer-lead">Share your link on WhatsApp. Every friend who signs up gives you <strong>6 extra free days</strong> — up to 30 in total — and they start with 5 welcome days of their own.</div>
            {refCode ? (
              <div className="vh-refer-row">
                <div className="vh-refer-link">{refLink}</div>
                <a className="vh-btn vh-btn-primary vh-btn-md" href={waHref} target="_blank" rel="noreferrer">Share on WhatsApp</a>
              </div>
            ) : (
              <div className="vh-refer-row">
                <Link to="/register" className="vh-btn vh-btn-primary vh-btn-md">Create your free account to get your invite link</Link>
              </div>
            )}
          </div>
          <div className="vh-refer-steps">
            {REFER_STEPS.map(([n, b]) => (
              <div className="vh-refer-step" key={n}>
                <div className="n">{n}</div>
                <div className="b">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8.5 · Become a tester — web live now, apps in testing */}
      <section className="vh-section vh-tester" id="vh-tester">
        <div className="vh-tester-card">
          <span className="vh-kicker">Testers get 7 free days</span>
          <h2 className="vh-tester-title">Be a Vault tester &mdash; <span className="hl">7 free days</span></h2>
          <p className="vh-tester-sub">
            Help shape what comes next. Tell us which device you use and we&rsquo;ll send you
            test builds of new versions first &mdash; with 7 bonus days on us.
          </p>
          {tsDone ? (
            <div className="vh-tester-done">
              <span className="vh-tester-done-badge" aria-hidden="true">&#10003;</span>
              <div>
                You&rsquo;re on the list &mdash; <strong>7 free days</strong> are yours. Watch your
                inbox for your device link. Meanwhile, <Link to="/register">start on the web now &rarr;</Link>
              </div>
            </div>
          ) : (
            <form className="vh-tester-form" onSubmit={joinTester}>
              <div className="vh-tester-devices" role="group" aria-label="Which device will you test?">
                {[['android', 'Android'], ['ios', 'iPhone / iPad'], ['both', 'Both'], ['web', 'Just web']].map(([v, label]) => (
                  <button type="button" key={v}
                          className={`vh-chip${tsPlatform === v ? ' is-on' : ''}`}
                          aria-pressed={tsPlatform === v}
                          onClick={() => setTsPlatform(v)}>{label}</button>
                ))}
              </div>
              <div className="vh-tester-fields">
                <input className="vh-input" value={tsName} onChange={(e) => setTsName(e.target.value)}
                       placeholder="Your name" aria-label="Your name" />
                <input className="vh-input" type="email" required value={tsEmail}
                       onChange={(e) => setTsEmail(e.target.value)}
                       placeholder="you@email.com" aria-label="Email address" />
                <input className="vh-input" value={tsPhone} onChange={(e) => setTsPhone(e.target.value)}
                       placeholder="Phone / WhatsApp (optional)" aria-label="Phone or WhatsApp" />
              </div>
              <label className="vh-tester-check">
                <input type="checkbox" checked={tsWa} onChange={(e) => setTsWa(e.target.checked)} />
                <span>Send me the daily WhatsApp recap</span>
              </label>
              <label className="vh-tester-check">
                <input type="checkbox" checked={tsConsent} onChange={(e) => setTsConsent(e.target.checked)} />
                <span>I agree to be contacted about testing (POPIA)</span>
              </label>
              <button type="submit" className="vh-btn vh-btn-primary vh-btn-lg vh-tester-submit">Sign me up &mdash; get 7 free days</button>
            </form>
          )}
          {tsErr && <div className="vh-wait-err">{tsErr}</div>}
          <div className="vh-tester-foot">
            Prefer to just dive in? <Link to="/register">Create your free account &rarr;</Link>
          </div>
        </div>
      </section>

      {/* 9 · Close / waitlist */}
      <section className="vh-close">
        <img src="/logo.svg" alt="" />
        <div className="t">Not ready to sign up? Stay in the loop.</div>
        <div className="p">One email when there&rsquo;s something worth telling you. Nothing else.</div>
        {wlDone ? (
          <div className="vh-wait-done">Thanks — you&rsquo;re on the list.</div>
        ) : (
          <form className="vh-wait" onSubmit={joinWaitlist}>
            <input type="email" required value={wlEmail} onChange={(e) => setWlEmail(e.target.value)}
                   placeholder="you@email.com" className="vh-input" aria-label="Email address" />
            <button type="submit" className="vh-btn vh-btn-primary vh-btn-lg">Keep me posted</button>
          </form>
        )}
        {wlErr && <div className="vh-wait-err">{wlErr}</div>}
        <div className="vh-foot">
          Vault · vault-app.co.za · <Link to="/privacy">Privacy (POPIA)</Link> · <Link to="/terms">Terms</Link> · your data is yours to export or delete, any time.
        </div>
      </section>
    </div>
  );
}
